import { useEffect, useState } from 'react';

/**
 * Notices a new deploy without a service worker. index.html is served with `no-cache` and names
 * the hashed bundle of the current build, so fetching it and comparing the bundle name tells
 * whether the running app is old. Phones keep a home-screen app open for days, so this matters.
 */
const CHECK_EVERY_MS = 10 * 60 * 1000;

export function bundleOf(html: string): string | null {
  return /<script[^>]+type="module"[^>]+src="([^"]+)"/.exec(html)?.[1] ?? null;
}

function runningBundle(): string | null {
  const src = document.querySelector<HTMLScriptElement>('script[type="module"][src]')?.src;
  return src ? new URL(src, window.location.href).pathname : null;
}

async function newerBuildExists(): Promise<boolean> {
  const running = runningBundle();
  // In development Vite serves /src/main.tsx: nothing to compare.
  if (!running?.startsWith('/assets/')) return false;
  try {
    const res = await fetch('/', { cache: 'no-store', headers: { Accept: 'text/html' } });
    if (!res.ok) return false;
    const latest = bundleOf(await res.text());
    return latest !== null && latest !== running;
  } catch {
    return false; // Offline: try again later.
  }
}

/**
 * True when a newer version is deployed. When the app comes back to the foreground and a newer
 * version exists, it reloads at once (nothing is typed at that moment); otherwise the screen can
 * show a "new version" button.
 */
export function useAppUpdate(): boolean {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let stopped = false;
    const check = async (reloadNow: boolean) => {
      if (!(await newerBuildExists()) || stopped) return;
      if (reloadNow) window.location.reload();
      else setReady(true);
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') void check(true);
    };
    document.addEventListener('visibilitychange', onVisible);
    const timer = window.setInterval(() => void check(false), CHECK_EVERY_MS);
    return () => {
      stopped = true;
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(timer);
    };
  }, []);
  return ready;
}
